package com.pincer.core.functions

import com.pincer.core.model.tree.Pincer
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.tree.Question
import com.pincer.core.model.tree.Answer
import com.pincer.core.model.tree.NAME_SCOPE
import com.pincer.core.model.tree.DESCRIPTION_SCOPE
import com.pincer.core.model.tree.CHATS_SCOPE
import com.pincer.core.model.tree.QUESTIONS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ANSWERS_SCOPE
import com.pincer.core.model.tree.SELECTED_SCOPE
import com.pincer.core.model.tree.CANDIDATES_SCOPE
import com.pincer.core.model.tree.PERMISSIONS_SCOPE
import com.pincer.core.model.tree.PARTICIPATIONS_SCOPE
import com.pincer.core.InvalidData
import com.pincer.core.contracts.TERMS_LAST_CHANGED_AT_MILLIS
import kotlin.time.Clock

fun readable(pid: String): String {
    return "" +
        pid.substring(0,3) + "-" +
        pid.substring(3,6) + "-" +
        pid.substring(6,9) + "-" +
        pid.substring(9,12)
}

fun readablePid(pincer: Pincer): String = readable(pincer.pid)

fun millisSince(pincer: Pincer, clock: Clock = Clock.System): Long {
    return clock.now().toEpochMilliseconds() - pincer.createdAt
}

fun millisBasedId(pincer: Pincer, prefix: Char, uniqueIn: Map<String, Any>, clock: Clock = Clock.System): String {
    val m = millisSince(pincer, clock)
    var i = 0
    while (i < 1000) {
        val id = prefix.plus((m+i).toString().padStart(15, '0'))
        if (uniqueIn[id] == null) return id
        i++
    }
    throw Exception("Can't find unique id")
}

private val millisIdPattern = Regex("^[A-Z](\\d+)$")
fun checkNewMillisId(id: String, pincer: Pincer) {
    try {
        val match = millisIdPattern.find(id)
        if (match == null) throw InvalidData("Bad millis id format? $id")
        val millis = match.groupValues.get(1).toLong()
        val now = millisSince(pincer)
        if (millis > now + 5*1000L) throw InvalidData("Millis id from the future? $id")
        if (millis < now - 60*1000L) throw InvalidData("Millis id to old? $id")
    }
    catch (nfe: NumberFormatException) { throw InvalidData("Bad millis id? $id") }
}

fun newChatId(pincer: Pincer)       = millisBasedId(pincer = pincer, prefix = 'C', uniqueIn = pincer.chats)
fun newQuestionId(pincer: Pincer)   = millisBasedId(pincer = pincer, prefix = 'Q', uniqueIn = pincer.questions)
fun newPermissionId(pincer: Pincer) = millisBasedId(pincer = pincer, prefix = 'M', uniqueIn = pincer.permissions)

/*
class MillisBasedIdComparator {
    companion object : Comparator<String> {
        override fun compare(a: String, b: String): Int = when {
            a.length != b.length -> a.compareTo(b)
            else -> a.length - b.length
        }
    }
}
*/

fun getRolesForParticipant(pincer: Pincer?, principalId: String?): String? {
    if (pincer == null || principalId == null) return null
    for (id in pincer.participants.keys) {
        if (id == principalId) {
            return pincer.participants[id]!!.roles
        }
    }
    return null
}

fun summaryForParticipant(pincer: Pincer, principalId: String): String? {
    val longVersion = StringBuilder()
    if (pincer.chats.size > 0) {
        val chat = pincer.chats.values.last()
        if (chat.principalId == principalId) {
            // TODO i18n ... we have their locale of course
            // just got to get that server-side all-locales translations set up
            longVersion.append("You")
        }
        else {
            longVersion.append(pincer.participants[chat.principalId]?.name ?: chat.principalId)
        }
        longVersion.append(": ${chat.wording}")
    }
    else if (pincer.description != null) {
        longVersion.append(pincer.description)
    }
    else if(pincer.participants.size == 1) {
        longVersion.append("just you so far")
    }
    else {
        // TODO, this could be "Fred, Bob and 3 others" (as many names as fit in 50 chars)
        longVersion.append(pincer.participants.size.toString())
        longVersion.append(" participants")
    }
    if (longVersion.length <= 50) return longVersion.toString()
    return longVersion.toString().substring(0,47) + "..."
}

fun getQuestionId(pincer: Pincer, questionIdOrCode: String): String? {
    for (questionId in pincer.questions.keys) {
        if (questionId == questionIdOrCode || pincer.questions[questionId]!!.code == questionIdOrCode) {
            return questionId
        }
    }
    return null
}

fun getQuestion(pincer: Pincer, questionIdOrCode: String): Question? {
    val questionId = getQuestionId(pincer = pincer, questionIdOrCode = questionIdOrCode)
    if (questionId == null) return null
    return pincer.questions[questionId]
}

fun getAnswerId(pincer: Pincer, questionIdOrCode: String, participant: Participant): String? {
    val questionId: String? = getQuestionId(pincer = pincer, questionIdOrCode = questionIdOrCode)
    if (questionId == null) {
        if (participant.answers[questionIdOrCode] != null) {
            // log.w("Orphan answer?")
            return questionIdOrCode
        }
        throw InvalidData("No question for that id/code")
    }
    if (participant.answers[questionId] != null) return questionId
    return null
}

fun getAnswer(pincer: Pincer, questionIdOrCode: String, participant: Participant): Answer? {
    val id = getAnswerId(pincer = pincer, questionIdOrCode = questionIdOrCode, participant = participant)
    if (id == null) return null
    return participant.answers[id]
}

fun upsertAnswer(pincer: Pincer, questionIdOrCode: String, participant: Participant, wording: String) {
    val questionId = getQuestionId(pincer, questionIdOrCode) ?: throw InvalidData("No question for that id/code")
    if (participant.answers[questionId] == null) {
        participant.answers[questionId] = Answer(wording = wording)
    }
    else {
        participant.answers[questionId]!!.wording = wording
    }
}

fun termsNeeded(lastAcceptedAtMillis: Long?): Boolean {
    if (lastAcceptedAtMillis == null) return true
    return lastAcceptedAtMillis < TERMS_LAST_CHANGED_AT_MILLIS
}

private val PRI = "pri_"
fun toPersonalPid(principalId: String) = principalId.substring(PRI.length)
fun fromPersonalPid(pid: String) = PRI + pid
